<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { customValidators } from "@/i18n/utils/i18n-validators";
import { ApiClientError } from "@shopware/api-client";

const { required, minLength, sameAs } = customValidators();
const emits = defineEmits<{
  (e: "success"): void;
}>();

const { updatePassword } = useCustomerPassword();
const { user, refreshUser } = useUser();

const errors = ref([]);
const userErrorMessages = computed(() =>
  errors.value?.map(({ detail }) => detail).toString()
);

const isSuccess = ref(false);
const loadingData = ref(false);

const state = reactive({
  password: {
    currentPassword: "",
    newPassword: "",
    newPasswordConfirm: "",
  },
  email: user.value?.email,
});

const rules = computed(() => ({
  password: {
    currentPassword: {
      required,
      minLength: minLength(8),
    },
    newPassword: {
      required,
      minLength: minLength(8),
    },
    newPasswordConfirm: {
      required,
      newPasswordConfirm: sameAs(state.password.newPassword),
    },
  },
}));

const $v = useVuelidate(rules, state);

const invokeChange = async (): Promise<void> => {
  loadingData.value = true;
  isSuccess.value = false;
  try {
    errors.value = [];
    const isFormCorrect = await $v.value.$validate();

    if (isFormCorrect) {
      const changePasswordResult = await updatePassword({
        password: state.password.currentPassword,
        newPassword: state.password.newPassword,
        newPasswordConfirm: state.password.newPasswordConfirm,
      });
      await refreshUser();

      if (changePasswordResult) {
        state.password.currentPassword = "";
        state.password.newPassword = "";
        state.password.newPasswordConfirm = "";

        $v.value.$reset();

        isSuccess.value = true;
        emits("success");
      }
    } else {
      return;
    }
  } catch (error) {
    console.error("[AccountChangePassword]", error);
    if (error instanceof ApiClientError) {
      errors.value = error.details?.errors || [];
    }
  } finally {
    loadingData.value = false;
  }
};
</script>
<template>
  <div class="py-10 border-0">
    <div class="font-bold text-sm md:text-lg lg:text-xl leading-normal mb-6">
      <div>{{ $t("account.password") }}</div>
    </div>
    <div
      v-if="isSuccess"
      class="p-4 mb-4 text-sm text-green-700 bg-green-100 rounded-lg dark:bg-green-200 dark:text-green-800"
      role="alert"
    >
      <span class="font-medium">{{
        $t("changePassword.messages.passwordUpdateSuccess")
      }}</span>
    </div>
    <form
      data-testid="account-change-password-form"
      @submit.prevent="invokeChange"
    >
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-6 md:gap-y-4 gap-x-4 md:gap-x-4 lg:gap-x-8 mb-4 md:mb-10"
      >
        <div class="relative">
          <input
            id="current-password"
            v-model="state.password.currentPassword"
            name="current-password"
            type="password"
            autocomplete="current-password"
            required
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.password.currentPassword.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            placeholder="••••••••"
            data-testid="account-change-current-password-input"
            @blur="$v.password.currentPassword.$touch()"
            :disabled="loadingData"
          />
          <label
            for="current-password"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{
              'transform -translate-y-5 text-xs px-2':
                state.password.currentPassword ||
                $v.password.currentPassword.$error,
              'text-red-500': $v.password.currentPassword.$error,
              'text-dark-bg': !$v.password.currentPassword.$error,
            }"
            >{{ $t("changePassword.form.currentPassword") }}</label
          >
          <span
            v-if="$v.password.currentPassword.$error"
            class="form-error text-xs"
          >
            {{ $v.password.currentPassword.$errors[0].$message }}
          </span>
        </div>
        <div class="relative">
          <input
            id="new-password"
            v-model="state.password.newPassword"
            name="new-password"
            type="password"
            autocomplete="new-password"
            required
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.password.newPassword.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            placeholder="••••••••"
            data-testid="account-change-new-password-input"
            @blur="$v.password.newPassword.$touch()"
            :disabled="loadingData"
          />
          <label
            for="new-password"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{
              'transform -translate-y-5 text-xs px-2':
                state.password.newPassword || $v.password.newPassword.$error,
              'text-red-500': $v.password.newPassword.$error,
              'text-dark-bg': !$v.password.newPassword.$error,
            }"
            >{{ $t("changePassword.form.newPassword") }}</label
          >
          <span
            v-if="$v.password.newPassword.$error"
            class="form-error text-xs"
          >
            {{ $v.password.newPassword.$errors[0].$message }}
          </span>
        </div>
        <div class="relative">
          <input
            id="confirm-password"
            v-model="state.password.newPasswordConfirm"
            name="confirm-password"
            type="password"
            autocomplete="off"
            required
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.password.newPasswordConfirm.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            placeholder="••••••••"
            data-testid="account-change-confirm-password-input"
            @blur="$v.password.newPasswordConfirm.$touch()"
            :disabled="loadingData"
          />
          <label
            for="confirm-password"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{
              'transform -translate-y-5 text-xs px-2':
                state.password.newPasswordConfirm ||
                $v.password.newPasswordConfirm.$error,
              'text-red-500': $v.password.newPasswordConfirm.$error,
              'text-dark-bg': !$v.password.newPasswordConfirm.$error,
            }"
            >{{ $t("changePassword.form.resetPassword") }}</label
          >
          <span
            v-if="$v.password.newPasswordConfirm.$error"
            class="form-error text-xs"
          >
            {{ $v.password.newPasswordConfirm.$errors[0].$message }}
          </span>
        </div>
      </div>

      <div class="flex justify-end">
        <button
          class="btn-default btn-red-bg"
          type="submit"
          data-testid="account-change-current-submit-button"
          :disabled="loadingData"
          :class="{
            'opacity-50 cursor-not-allowed': loadingData,
          }"
        >
          {{ $t("changePassword.form.changePassword") }}
          <span class="ml-2" v-if="loadingData">
            <i class="fa-solid fa-spinner animate-spin"></i>
          </span>
        </button>
      </div>

      <div
        v-if="userErrorMessages?.length"
        class="text-red-600 text-sm focus:ring-primary border-secondary-300 rounded"
      >
        {{ userErrorMessages }}
      </div>
    </form>
  </div>
</template>
